import {useMutation, useQuery, useQueryClient} from "@tanstack/react-query";
import {message} from "antd";
import {AxiosError} from "axios";
import {TUserObjApiResponse} from "../redux/auth/i-auth";
import {TFetchCustomerBookingPayload} from "../redux/booking/i-time-slots-payload";
import {TBooking} from "../redux/call/i-call";
import {PaginationMeta, TServiceApiResponse} from "../redux/service/i-service";
import {AnalyticsEvent} from "../utils/analytics";
import {useClientV2} from "../utils/hooks";
import {TLiveClassApiResponse} from "./live-events/live-classes";
import {upcomingCallsQueryKey} from "./session/upcoming-calls";
import {normalizeV2BookingJoinResponse} from "./booking/booking-join-normalizer";
import {normalizeV2PublicLiveClassJoinResponse} from "./booking/live-class-join-normalizer";
import {useCoachPastCallsLegacy} from "../features/past-calls";
import {createBookingActionsRepository} from "../features/booking-actions";

type TLiveClassBooking = {
  id: number;
  userId: number;
  eventId: number;
  paymentProvider: string;
  roomId: string | null;
  current_session: number;
  firstName: string;
  lastName: string;
  emailAddress: string;
  bookingQuestions: string;
  is_series: boolean;
  is_paid: boolean;
  event_date: Date;
  reference: string;
  call_duration: number;
  coach_event_time: string;
  coach_end_time: string;
  customer_event_time: string;
  customer_end_time: string;
  customer_timezone: string;
  statusId: number;
  createdAt: Date;
  updatedAt: Date;
  startDate: Date;
  startTime: string;
  statusName: string;
  endTime: string;
};

type TLiveClassCoach = {
  firstName: string;
  lastName: string;
};

export type TPastcallsApiResponse = {
  bookings: TBooking[];
};

export type TCustomerBookingResponse = {
  booking: TBooking;
  service: TServiceApiResponse;
  mentor: Omit<TUserObjApiResponse, "password" | "emailAddress"> & {
    email: string;
  };
  timeZone: string;
  unavailableDates: {
    from: string;
    to: string;
  }[];
  days: {
    sun: {from: string; to: string}[];
    mon: {from: string; to: string}[];
    tue: {from: string; to: string}[];
    wed: {from: string; to: string}[];
    thu: {from: string; to: string}[];
    fri: {from: string; to: string}[];
    sat: {from: string; to: string}[];
  };
  bookingTimeSlot: string;
  bookingToken: string | null;
  videoPlatform: "dailyCo" | "videoSdk" | "googleMeet" | "custom";
  dailyCoUrl: string;
};
export type TCustomerLiveClassBookingResponse = {
  eventBooking: TLiveClassBooking;
  event: TLiveClassApiResponse;
  bookingToken: string | null;
  coach: TLiveClassCoach;
  videoPlatform: "dailyCo" | "videoSdk" | "googleMeet" | "custom";
  dailyCoUrl: string;
  customRoomLink?: string | null;
  actorType?: string;
  customersTimezone?: string | null;
  coachesTimezone?: string | null;
};

export type TCoachRescheduleCallPayload = {
  bookingReference: string;
  booking_year: string | number;
  booking_month: string | number;
  booking_date: string | number;
  timeSlot: string;
  description?: string;
};

/**
 * Coach "Past sessions" list.
 *
 * Migrated to the past-calls feature slice (v2 endpoint `bookings/past`, gated by
 * the V2_PAST_CALLS flag). This is now a thin strangler bridge that delegates to
 * {@link useCoachPastCallsLegacy}, which returns the historical
 * `{ data: { bookings }, meta }` shape so consuming UI stays unchanged.
 *
 * @deprecated Prefer `useCoachPastCalls` from "../features/past-calls".
 */
const useGetPastCalls = (page = 1, per_page = 5) =>
  useCoachPastCallsLegacy(page, per_page);

/**
 * Reason sent with a coach cancellation from a call site that has no reason field.
 *
 * v1's coach cancel took no body at all. v2's `CancelBookingDto.reason` is
 * required, must be non-empty, and is shown to the customer, so something has to
 * be sent. (`useCoachCancelUpcomingCall` in session/upcoming-calls does collect a
 * reason from the coach and passes it straight through.)
 */
const COACH_CANCEL_FALLBACK_REASON = "The coach cancelled this session.";

/**
 * Coach-initiated cancellation of one booking.
 *
 * Now a thin bridge over the booking-actions slice, which owns the v2 call
 * (`PUT bookings/:reference/cancel`). The signature, cache key, optimistic
 * rollback, toasts and analytics are unchanged from the v1 version, so its call
 * sites need no edits.
 */
const useCoachCancelBooking = () => {
  const queryClient = useQueryClient();
  const repository = createBookingActionsRepository();
  return useMutation({
    mutationFn: (bookingRef: string) =>
      repository.cancelBooking({
        actor: "coach",
        reference: bookingRef,
        reason: COACH_CANCEL_FALLBACK_REASON,
      }),
    onMutate: () => {
      const oldBookings = queryClient.getQueryData(["upcoming_calls"]);

      return () => queryClient.setQueryData(["upcoming_calls"], oldBookings);
    },
    onError: (error: AxiosError<{message: string}>, values, rollback) => {
      message.error(error.message);
      AnalyticsEvent("Coach cancel booking", "Coach cancel booking fail");
      if (typeof rollback === "function") {
        rollback();
      }
    },
    onSuccess: () => {
      AnalyticsEvent("Coach cancel booking", "Coach cancel booking success");
    },
    onSettled: () => queryClient.invalidateQueries({queryKey: ["upcoming_calls"]}),
  });
};

/**
 * Meeting-join details for a booking the signed-in customer owns.
 *
 * v1 served one route, `customer-booking/:reference/:userType`, which returned the
 * whole booking graph and took the actor as a path segment. v2 split it by actor -
 * `bookings/:reference/join` for the coach, `customer/bookings/:reference/join` for
 * the customer - and reads identity off the bearer token, so the discriminator is
 * gone. Only the customer half has a live caller (the coach-side caller,
 * RescheduleCallModal, was unreachable and has been deleted), so no coach twin is
 * exported here; the coach mutation path uses useGetCustomerBookingMutation, which
 * already branches on both namespaces.
 *
 * Shape caveat, verified against the backend: v2's JoinBookingResponseDto is
 * `{token, roomId, videoPlatform}` and nothing else. normalizeV2BookingJoinResponse
 * rebuilds the legacy nested shape around those three fields and stubs the rest
 * (service, mentor, availability, dailyCoUrl) with empty values. No v2 route returns
 * the booking graph v1 returned, and none of them answers unauthenticated.
 */
const useGetCustomerBooking = (
  payload: {reference?: string},
  options?: {
    refetchInterval?:
      | number
      | false
      | ((data: TCustomerBookingResponse | undefined) => number | false);
  }
) => {
  const clientV2 = useClientV2();

  return useQuery<
    TCustomerBookingResponse,
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: ["customer_booking", payload.reference],
    queryFn: async () => {
      const response = await clientV2(`customer/bookings/${payload.reference}/join`);
      const inner = response?.data ?? response;
      if (inner) {
        return normalizeV2BookingJoinResponse(inner);
      }
      throw new Error(response.message);
    },
    enabled: !!payload.reference,
    refetchInterval: options?.refetchInterval,
  });
};

const useGetCustomerBookingMutation = () => {
  const clientV2 = useClientV2();
  return useMutation({
    mutationFn: async (payload: TFetchCustomerBookingPayload) => {
      const endpoint =
        payload.userType === "host_user"
          ? `bookings/${payload.reference}/join`
          : `customer/bookings/${payload.reference}/join`;
      const response = await clientV2(endpoint, {method: "GET"});
      const inner = response?.data ?? response;
      return {
        ...response,
        data: normalizeV2BookingJoinResponse(inner),
      };
    },
    onError: (error: AxiosError<{message: string}>) => {
      message.error(error.message);
    },
  });
};

const useGetCustomerLiveClassBooking = (
  payload: {reference?: string},
  options?: {
    refetchInterval?:
      | number
      | false
      | ((data: TCustomerLiveClassBookingResponse | undefined) => number | false);
  }
) => {
  const clientV2 = useClientV2();

  return useQuery<
    TCustomerLiveClassBookingResponse,
    AxiosError<{status: boolean; message: string}>
  >({
    queryKey: ["customer_live_class_booking", payload.reference],
    queryFn: async () => {
      const response = await clientV2(`public/live-event/join/${payload.reference}`, {
        token: undefined,
      });
      if (response.data) {
        return normalizeV2PublicLiveClassJoinResponse(response.data);
      }
      throw new Error(response.message);
    },
    enabled: !!payload.reference,
    refetchInterval: options?.refetchInterval,
  });
};

const useCoachRescheduleCall = () => {
  const queryClient = useQueryClient();
  const repository = createBookingActionsRepository();
  return useMutation({
    mutationFn: (payload: TCoachRescheduleCallPayload) =>
      repository.rescheduleBooking({
        actor: "coach",
        reference: payload.bookingReference,
        bookingYear: payload.booking_year,
        bookingMonth: payload.booking_month,
        bookingDate: payload.booking_date,
        timeSlot: payload.timeSlot,
        reason: payload.description ?? "",
      }),
    onError: (error: AxiosError<{message: string}>) => {
      message.error(error.message);
      AnalyticsEvent("Coach reschedule booking", "Coach reschedule booking fail");
    },
    onSuccess: (d, v) => {
      AnalyticsEvent("Coach reschedule booking", "Coach reschedule booking success");
      // queryClient.removeQueries({
      //   exact: true,
      //   queryKey: ["customer_booking", v.bookingReference],
      // });
    },
    onSettled: () => queryClient.invalidateQueries({queryKey: [upcomingCallsQueryKey]}),
  });
};

export {
  useGetPastCalls,
  useCoachCancelBooking,
  useGetCustomerBooking,
  useGetCustomerBookingMutation,
  useGetCustomerLiveClassBooking,
  useCoachRescheduleCall,
};
